import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { FieldStack } from '@/components/FieldStack'
import { Field } from '@/components/Field'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { Chip } from '@/components/Chip'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { AUTH_BRAND, A_INVITE_COPY } from '@/copy/registry'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'

// `OrgRole` → `chip` variant — the same mapping `v-users`'s member table uses (its own prototype
// evidence: "Owner"/"Admin"/"Operator"/"Auditor" each draw a different real chip), applied here to
// the ROLE the chip's colour actually represents (`preview.data.org_role`), independent of whether
// the chip's own TEXT is that value or the friendlier `role_summary` string.
const ORG_ROLE_CHIP: Record<string, 'type' | 'pending' | 'off'> = { OWNER: 'type', ADMIN: 'pending', OPERATOR: 'off', AUDITOR: 'off' }

/**
 * `a-invite` — process/09-SCREEN-SPECS/a-invite.md, §3.1i rule 11. Previews without claiming
 * (`identityPreviewInvitation`, fires on entry); a refused preview resolves the dead-link landing
 * (`identityGetInvitationStatus`). `requires` is server-decided — the client never guesses signup
 * vs signin.
 *
 * GAP reported rather than worked around: `SignUpRequest` requires `display_name`, and the
 * prototype's invite-signup form collects only a password — no name field. This component adds
 * one (labelled from `A_SIGNUP_COPY`'s own field, not invented text) rather than submitting a
 * fabricated value; the prototype's drawn structure did not carry this field and this is a
 * deviation from it, recorded here rather than silently matched.
 *
 * **The role chip recoloured from a hardcoded `off` to a real per-role mapping (W173)** — the
 * prototype's own example happens to show an `Operator` invite, which IS `off`, so the hardcoded
 * value was coincidentally right for that one role and wrong for the other three.
 */
export function AInvite({
  invitation,
  onSignedUpForAccept,
  onGoToSignInToAccept,
  onDead,
}: {
  invitation: string
  onSignedUpForAccept: (email: string, invitation: string) => void
  onGoToSignInToAccept: (invitation: string) => void
  onDead: () => void
}) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [name, setName] = useState('')

  const preview = useQuery({
    queryKey: ['invitation-preview', invitation],
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.POST('/auth/invitations/preview', { body: { invitation } })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
    retry: false,
  })

  const checkStatus = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/invitations/status', { body: { invitation } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSettled: onDead,
  })

  useEffect(() => {
    if (preview.isError) checkStatus.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.isError])

  const signUp = useMutation({
    mutationFn: async () => {
      if (!preview.data) throw new Error('unreachable: signup submitted before preview resolved')
      const client = getApiClient()
      const { error } = await client.POST('/auth/sign-up', {
        body: {
          // Ignored server-side when `invitation` is present (SignUpRequest.yaml) — reusing the
          // already-served organisation name here satisfies the schema's required field without
          // originating a value; the server never reads it in this branch.
          organisation_name: preview.data.organisation_name,
          display_name: name,
          email: preview.data.email,
          password,
          invitation,
        },
      })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSuccess: () => onSignedUpForAccept(preview.data!.email, invitation),
  })

  const mismatch = password.length > 0 && confirmPassword.length > 0 && password !== confirmPassword
  const signUpProblem = signUp.error instanceof ApiProblemError ? signUp.error.problem : undefined

  if (preview.isLoading || checkStatus.isPending) {
    return (
      <AuthCard unitId={unitId('a-invite')}>
        <AuthCardBrand label={AUTH_BRAND} />
        <h3 className="m-0 mb-3 text-section font-semibold">{A_INVITE_COPY.heading}</h3>
        <Note>Loading…</Note>
      </AuthCard>
    )
  }

  if (preview.isError || !preview.data) {
    return (
      <AuthCard unitId={unitId('a-invite')}>
        <AuthCardBrand label={AUTH_BRAND} />
        <h3 className="m-0 mb-3 text-section font-semibold">{A_INVITE_COPY.heading}</h3>
        <Note>Resolving this link…</Note>
      </AuthCard>
    )
  }

  const { organisation_name, role_summary, email, requires } = preview.data

  return (
    <AuthCard unitId={unitId('a-invite')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_INVITE_COPY.heading}</h3>
      <AuthCardLead>
        <b>{email}</b>
        {A_INVITE_COPY.leadWasInvitedTo}
        <b>{organisation_name}</b>
        {A_INVITE_COPY.leadAs}
        <Chip variant={ORG_ROLE_CHIP[preview.data.org_role] ?? 'off'}>{role_summary ?? preview.data.org_role}</Chip>
      </AuthCardLead>
      <Note>{A_INVITE_COPY.scopeNote}</Note>

      {requires === 'signin' ? (
        <div className="mt-14">
          <Button data-act={actionId('nav.a-signin')} onClick={() => onGoToSignInToAccept(invitation)}>
            {A_INVITE_COPY.acceptSigninButton}
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-9">
            <Strip>{A_INVITE_COPY.existingUserStrip}</Strip>
          </div>
          <FieldStack>
            <Field label={A_INVITE_COPY.nameLabel}>
              <input className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={A_INVITE_COPY.passwordLabel}>
              <input type="password" className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <Field label={A_INVITE_COPY.confirmPasswordLabel}>
              <input
                type="password"
                className={TEXT_INPUT_CLASSNAME}
                style={TEXT_INPUT_STYLE}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Field>
          </FieldStack>
          <Note>{A_INVITE_COPY.passwordRule}</Note>

          {mismatch && <FormError>{A_INVITE_COPY.passwordMismatch}</FormError>}
          {signUpProblem && <AInviteRefusal problem={signUpProblem} />}

          <Button
            data-act={actionId('invitation.accept')}
            disabled={signUp.isPending || mismatch || !name || !password || !confirmPassword}
            onClick={() => signUp.mutate()}
          >
            {signUp.isPending ? 'Creating…' : A_INVITE_COPY.acceptSignupButton}
          </Button>
        </>
      )}
    </AuthCard>
  )
}

function AInviteRefusal({ problem }: { problem: Problem }) {
  if (problem.refusal_kind === 'VALIDATION_FAILED') {
    return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
  }
  if (problem.refusal_kind === 'RATE_LIMITED') return <RateLimitedNotice problem={problem} />
  return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
}
